import { Button, Modal, Form, Input, Switch, Table } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { PageHeader } from '../../components/common/PageHeader';
import { confirmAction } from '../../components/common/ConfirmAction';
import { useFeatureFlags, useCreateFeatureFlag, useUpdateFeatureFlag, useDeleteFeatureFlag } from '../../hooks/useSystem';
import { usePermissions } from '../../hooks/usePermissions';
import dayjs from 'dayjs';
import type { AdminFeatureFlagItem } from '../../types/system';
import type { ColumnsType } from 'antd/es/table';

export function FeatureFlagsPage() {
  const { data, isLoading } = useFeatureFlags();
  const create = useCreateFeatureFlag();
  const update = useUpdateFeatureFlag();
  const del = useDeleteFeatureFlag();
  const { canManageFeatureFlags } = usePermissions();
  const [showCreate, setShowCreate] = useState(false);
  const [form] = Form.useForm();

  const columns: ColumnsType<AdminFeatureFlagItem> = [
    { title: 'Key', dataIndex: 'key', key: 'key' },
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Enabled', dataIndex: 'enabled_globally', key: 'enabled', render: (v: boolean, r) => <Switch checked={v} disabled={!canManageFeatureFlags} onChange={(checked) => update.mutate({ id: r.id, data: { enabled_globally: checked } })} /> },
    { title: 'Orgs Enabled', dataIndex: 'enabled_count', key: 'count' },
    { title: 'Updated', dataIndex: 'updated_at', key: 'updated', render: (v: string) => dayjs(v).format('MMM D, YYYY') },
    ...(canManageFeatureFlags ? [{ title: 'Action', key: 'action', render: (_: unknown, r: AdminFeatureFlagItem) => <Button size="small" danger onClick={() => confirmAction({ title: 'Delete Flag', content: `Delete "${r.key}"?`, onConfirm: () => del.mutate(r.id), danger: true })}>Delete</Button> }] : []),
  ];

  const handleCreate = () => {
    form.validateFields().then((values) => {
      create.mutate(values);
      setShowCreate(false);
      form.resetFields();
    });
  };

  return (
    <>
      <PageHeader title="Feature Flags" extra={canManageFeatureFlags && <Button type="primary" icon={<PlusOutlined />} onClick={() => setShowCreate(true)}>New Flag</Button>} />
      <Table<AdminFeatureFlagItem> columns={columns} dataSource={data} loading={isLoading} rowKey="id" size="middle" pagination={false} />
      <Modal title="Create Feature Flag" open={showCreate} onOk={handleCreate} onCancel={() => setShowCreate(false)}>
        <Form form={form} layout="vertical">
          <Form.Item name="key" label="Key" rules={[{ required: true }]}><Input placeholder="e.g., enable_new_dashboard" /></Form.Item>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="description" label="Description"><Input.TextArea rows={2} /></Form.Item>
          <Form.Item name="enabled_globally" label="Enabled Globally" valuePropName="checked"><Switch /></Form.Item>
        </Form>
      </Modal>
    </>
  );
}
